import { useState } from "react";
import { Link } from "react-router-dom";
import { PageRenderer } from "@/components/sections/PageRenderer";
import { SectionTitle } from "@/components/common/SectionTitle";
import { useSite } from "@/context/SiteContext";
import { cn } from "@/lib/utils";
import { track, EVENT } from "@/lib/track";
import {
  Calendar, CheckCircle2, ExternalLink, UserPlus, LogIn, LifeBuoy,
  CalendarCheck, MessageSquare, Package, Smartphone, ArrowRight,
} from "lucide-react";

// Simple guidance steps
const INSTRUCTIONS = [
  {
    icon: UserPlus,
    title: "New to Trilogy?",
    text: "Book an Intro Workout — your first session is free.",
    cta: "Book Intro",
    type: "intro",
    testid: "instruction-intro",
  },
  {
    icon: LogIn,
    title: "Already a client?",
    text: "Login to the app / portal to schedule sessions and classes.",
    cta: "Client Login",
    type: "portal",
    testid: "instruction-client",
  },
  {
    icon: LifeBuoy,
    title: "Need help?",
    text: "Submit a support ticket and our team will get back to you.",
    cta: "Get Support",
    type: "support",
    testid: "instruction-support",
  },
];

export default function Booking() { return <PageRenderer pageKey="booking" />; }

export const BookingBody = () => {
  const { settings } = useSite();
  const [loc, setLoc] = useState("lenexa");

  const embedUrl =
    loc === "lenexa"
      ? settings.booking_embed_lenexa_url
      : settings.booking_embed_independence_url;

  const introUrl = settings.booking_intro_url || settings.exercise_booking_url;
  const consultUrl = settings.booking_consultation_url || settings.exercise_booking_url;
  const portalUrl = settings.client_portal_url || "https://train.trilogyfitpt.com";

  // Resolve instruction CTA targets
  const instrHref = (type) =>
    type === "intro" ? introUrl : type === "portal" ? portalUrl : null;

  // Primary booking option cards
  const OPTIONS = [
    {
      icon: CalendarCheck,
      title: "Book Intro Workout",
      text: "For new leads. Meet a coach, get assessed, and start training.",
      href: introUrl,
      label: "Book Intro",
      external: true,
      featured: true,
      testid: "option-intro",
    },
    {
      icon: MessageSquare,
      title: "Book a Consultation",
      text: "Talk through your goals and find the right program for you.",
      href: consultUrl,
      label: "Book Consultation",
      external: true,
      testid: "option-consultation",
    },
    {
      icon: Smartphone,
      title: "Client Login / App Booking",
      text: "Current clients: schedule sessions inside the Trilogy app / portal.",
      href: portalUrl,
      label: "Open Portal",
      external: true,
      testid: "option-portal",
    },
    {
      icon: Package,
      title: "Package Purchase",
      text: "Compare memberships and check out securely on Exercise.com.",
      to: "/packages",
      label: "View Packages",
      external: false,
      testid: "option-packages",
    },
  ];

  return (
    <div data-testid="booking-page">
      {/* hero via PageRenderer */}

      <section className="bg-background py-16 sm:py-20">
        <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
          {/* Instructions */}
          <div id="booking-start" className="scroll-mt-24 grid gap-4 sm:grid-cols-3">
            {INSTRUCTIONS.map((s) => {
              const Icon = s.icon;
              const href = instrHref(s.type);
              return (
                <div
                  key={s.title}
                  data-testid={s.testid}
                  className="flex flex-col rounded-xl border border-border bg-card p-6"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-md bg-brand text-white">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-condensed text-lg font-semibold uppercase tracking-wide">{s.title}</h3>
                  <p className="mt-1.5 flex-1 text-sm text-muted-foreground">{s.text}</p>
                  {s.type === "support" ? (
                    <Link
                      to="/contact"
                      data-testid={`${s.testid}-cta`}
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-brand hover:underline"
                    >
                      {s.cta} <ArrowRight className="h-4 w-4" />
                    </Link>
                  ) : (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`${s.testid}-cta`}
                      onClick={() => track(EVENT.BOOKING_CLICK, s.title)}
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-brand hover:underline"
                    >
                      {s.cta} <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>
              );
            })}
          </div>

          {/* Booking options */}
          <div id="booking-options" className="scroll-mt-24 mt-14">
            <SectionTitle
              eyebrow="Choose How To Book"
              title="Booking Options"
              subtitle="Pick the option that fits you. Everything routes to our secure Exercise.com booking."
            />
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {OPTIONS.map((o) => {
                const Icon = o.icon;
                const btnClass = cn(
                  "mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-2.5 text-xs font-semibold uppercase tracking-wide transition-colors duration-200 active:scale-[0.98]",
                  o.featured
                    ? "bg-brand text-white hover:bg-[hsl(var(--brand-red-deep))]"
                    : "border border-border bg-transparent text-foreground hover:bg-accent"
                );
                return (
                  <div
                    key={o.title}
                    data-testid={`booking-${o.testid}`}
                    className={cn(
                      "flex flex-col rounded-xl border bg-card p-6",
                      o.featured ? "border-[hsl(var(--brand-red))]" : "border-border"
                    )}
                  >
                    <div className={cn("flex h-11 w-11 items-center justify-center rounded-md", o.featured ? "bg-brand text-white" : "bg-muted text-brand")}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-4 display-title text-2xl leading-tight">{o.title}</h3>
                    <p className="mt-2 flex-1 text-sm text-muted-foreground">{o.text}</p>
                    {o.external ? (
                      <a href={o.href} target="_blank" rel="noopener noreferrer" data-testid={`booking-${o.testid}-btn`} onClick={() => track(EVENT.BOOKING_CLICK, o.title)} className={btnClass}>
                        {o.label} <ExternalLink className="h-4 w-4" />
                      </a>
                    ) : (
                      <Link to={o.to} data-testid={`booking-${o.testid}-btn`} className={btnClass}>
                        {o.label} <ArrowRight className="h-4 w-4" />
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Embedded booking flow with fallback */}
          <div id="booking-scheduler" className="scroll-mt-24 mt-14">
            <SectionTitle
              eyebrow="Live Scheduler"
              title="Book Online Now"
              subtitle="Browse open times and book directly. Choose your location to get started."
            />

            {/* Location toggle */}
            <div className="mt-6 flex flex-wrap gap-2" role="tablist" aria-label="Select location">
              {[
                { id: "lenexa", label: "Lenexa, KS" },
                { id: "independence", label: "Independence, MO" },
              ].map((l) => (
                <button
                  key={l.id}
                  type="button"
                  role="tab"
                  aria-selected={loc === l.id}
                  onClick={() => setLoc(l.id)}
                  data-testid={`booking-loc-${l.id}`}
                  className={cn(
                    "rounded-full border px-4 py-2 font-condensed text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-200 active:scale-[0.98]",
                    loc === l.id
                      ? "border-[hsl(var(--brand-red))] bg-brand text-white"
                      : "border-border bg-transparent text-muted-foreground hover:border-[hsl(var(--brand-red))] hover:text-foreground"
                  )}
                >
                  {l.label}
                </button>
              ))}
            </div>

            <div className="mt-6 overflow-hidden rounded-xl border border-border">
              {embedUrl ? (
                <>
                  {/* Desktop: embedded iframe scheduler */}
                  <div className="hidden lg:block">
                    <iframe
                      key={embedUrl}
                      title="Trilogy Online Booking"
                      src={embedUrl}
                      loading="lazy"
                      className="h-[720px] w-full border-0 bg-white"
                      data-testid="booking-iframe"
                    />
                  </div>
                  {/* Mobile: embed is heavy — clean fallback button */}
                  <div className="flex flex-col items-center gap-4 bg-muted/30 p-10 text-center lg:hidden">
                    <Calendar className="h-12 w-12 text-brand" />
                    <h3 className="display-title text-3xl">Open The Scheduler</h3>
                    <p className="max-w-sm text-sm text-muted-foreground">For the best experience on mobile, open the booking scheduler in a new tab.</p>
                    <a
                      href={embedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="booking-embed-mobile-btn"
                      className="inline-flex items-center gap-2 rounded-md bg-brand px-8 py-4 text-base font-semibold uppercase tracking-wide text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors"
                    >
                      Book Now <ExternalLink className="h-5 w-5" />
                    </a>
                  </div>
                </>
              ) : (
                // Fallback when no embed URL is configured — site never breaks
                <div className="flex flex-col items-center gap-4 bg-muted/30 p-10 text-center" data-testid="booking-embed-fallback">
                  <Calendar className="h-12 w-12 text-brand" />
                  <h3 className="display-title text-3xl">Book Your Session</h3>
                  <p className="max-w-lg text-sm text-muted-foreground">Open our secure booking portal on Exercise.com to choose a time.</p>
                  <a
                    href={introUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="booking-fallback-btn"
                    className="inline-flex items-center gap-2 rounded-md bg-brand px-8 py-4 text-base font-semibold uppercase tracking-wide text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors"
                  >
                    Open Booking Portal <ExternalLink className="h-5 w-5" />
                  </a>
                </div>
              )}
            </div>

            {/* Always-available open-in-new-tab link */}
            <p className="mt-4 flex flex-col items-center gap-2 text-center text-xs text-muted-foreground sm:flex-row sm:justify-center">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-brand" /> Powered by Exercise.com · train.trilogyfitpt.com</span>
              {embedUrl && (
                <a href={embedUrl} target="_blank" rel="noopener noreferrer" data-testid="booking-open-newtab" className="font-semibold text-brand hover:underline">Having trouble? Open in a new tab</a>
              )}
            </p>
            <p className="mt-3 text-center text-xs text-muted-foreground" data-testid="booking-policy-note">
              First-time clients complete a{" "}
              <Link to="/policy/liability-waiver" className="text-brand hover:underline">Liability Waiver</Link> and{" "}
              <Link to="/policy/health-intake" className="text-brand hover:underline">Health History / Intake Form</Link>. Bookings are subject to our{" "}
              <Link to="/policy/cancellation" className="text-brand hover:underline">Cancellation Policy</Link>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
